import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/browser";
import { V2Alert, V2Button, V2TextField } from "@stacklet/ui";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-relay/hooks";
import { ConnectionHandler } from "relay-runtime";

import { NON_SSO_ROLE } from "app/constants";
import { useFormErrors } from "app/hooks";
import { AddUserMutation } from "app/mutations";

import { schema } from "./schema";

import type { FilterElementInput } from "../__generated__/UserListRefetchQuery.graphql";
import type {
  AddUserInput,
  AddUserMutation as AddUserMutationType,
} from "app/mutations/__generated__/AddUserMutation.graphql";

interface Props {
  onAdded: (message: string) => void;
  onCancel: () => void;
  filterElement?: FilterElementInput;
}

export function UpsertUserForm({ onAdded, onCancel, filterElement }: Props) {
  const [formErrors, setFormErrors] = useFormErrors();

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<AddUserInput>({
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  const [addUser, isInFlight] =
    useMutation<AddUserMutationType>(AddUserMutation);

  const addUserAction = (data: AddUserInput) => {
    const connectionId = ConnectionHandler.getConnectionID(
      "client:root",
      "UserList_users",
      {
        filterElement: filterElement ?? null,
      },
    );

    const { name, email } = data;
    const formattedUsername = email.replace("@", "_at_");

    addUser({
      variables: {
        input: {
          name,
          username: formattedUsername,
          email,
          roles: [NON_SSO_ROLE],
        },
        connections: [connectionId],
      },
      onCompleted(response, errors) {
        if (errors) {
          setFormErrors(errors.map((error) => error.message));
        }
        if (response.addUser?.user) {
          onAdded(`Added user ${response.addUser.user.name} successfully`);
        }
      },
      onError: (error) => {
        Sentry.captureException(error);
      },
    });
  };

  return (
    <div>
      <form
        className="mt-2 min-w-[500px] text-legacy-navy-L30"
        onSubmit={handleSubmit(addUserAction)}
      >
        <p className="mb-lg">
          The new admin will receive an email with a temporary password to
          activate their account.
        </p>
        <Controller
          control={control}
          defaultValue=""
          name="name"
          render={({ field, fieldState }) => (
            <V2TextField
              className="w-full"
              inputProps={{
                placeholder: "Enter the name for this admin",
              }}
              isInvalid={fieldState.invalid}
              label="Name *"
              isRequired
              {...field}
            />
          )}
        />

        <Controller
          control={control}
          defaultValue=""
          name="email"
          render={({ field, fieldState }) => (
            <V2TextField
              className="mt-xl w-full"
              inputProps={{
                placeholder: "Enter the email address for this admin",
              }}
              isInvalid={fieldState.invalid}
              label="Email address *"
              isRequired
              {...field}
            />
          )}
        />

        {formErrors
          ? formErrors.map((error) => (
              <div className="mt-xl" key={error.id}>
                <V2Alert severity="error">{error.message}</V2Alert>
              </div>
            ))
          : null}
        <div className="mb-lg mt-xl flex justify-between">
          <V2Button onPress={onCancel} variant="secondary">
            Cancel
          </V2Button>
          <V2Button
            data-testid="create-user-button"
            isDisabled={!isValid || isInFlight}
            isPending={isInFlight}
            pendingText="Creating and sending invite…"
            type="submit"
            variant="primary"
          >
            Create and send invite
          </V2Button>
        </div>
      </form>
    </div>
  );
}
